<template>
  <div
    class="cancellation-statements-component"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <!-- Drag overlay -->
    <div v-if="isDraggingOver && !isConsolidatedView" class="drag-overlay">
      <div class="drag-overlay-content">
        <font-awesome-icon
          :icon="['fas', 'cloud-upload-alt']"
          class="drag-overlay-icon"
        />
        <p class="drag-overlay-text">
          {{ $t("cancellation.drop_to_create") }}
        </p>
        <small class="text-muted">
          {{ $t("cancellation.upload.supported_formats") }}
        </small>
      </div>
    </div>

    <!-- Add new cancellation statement button -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">{{ $t("cancellation.list_title") }}</h5>
      <div class="d-flex align-items-center gap-2">
        <template v-if="cancellations.length > 0">
          <small
            v-if="bulkDownloadBlockedReason"
            class="bulk-download-blocked-reason"
            :class="bulkDownloadBlockedReason.className"
          >
            {{ bulkDownloadBlockedReason.text }}
          </small>
          <button
            class="btn btn-outline-primary bulk-download-button"
            :disabled="!canStartBulkDownload || isLoading"
            @click="startBulkDownload"
          >
            <font-awesome-icon
              :icon="['fas', isBulkDownloading ? 'spinner' : 'download']"
              class="me-2"
              :class="{ 'fa-spin': isBulkDownloading }"
            />
            {{ bulkDownloadLabel }}
          </button>
        </template>
        <!-- Untrusted companies cannot add statements (server enforces this too); the wrapping
             span carries the tooltip because disabled buttons don't emit hover events. -->
        <span
          v-if="!isConsolidatedView && !canAddStatement"
          :title="$t('cancellation.not_trusted_tooltip')"
          class="d-inline-block"
          tabindex="0"
        >
          <button class="btn btn-primary" disabled>
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="me-2" />
            {{ $t("cancellation.add_new_statement") }}
          </button>
        </span>
        <button
          v-else-if="!isConsolidatedView"
          class="btn btn-primary"
          @click="openAddCancellationModal"
        >
          <font-awesome-icon :icon="['fas', 'plus-circle']" class="me-2" />
          {{ $t("cancellation.add_new_statement") }}
        </button>
      </div>
    </div>

    <!-- Placeholder when no cancellations -->
    <PlaceholderCardComponent
      v-if="cancellations.length === 0 && !isLoading"
      :title="$t('cancellation.no_cancellations')"
      :subtitle="$t('cancellation.no_cancellations_info')"
    />

    <!-- Loading state -->
    <LoadingIndicatorComponent v-if="isLoading" />

    <!-- Cancellations table -->
    <CancellationStatementsTable
      v-else-if="cancellations.length > 0"
      :cancellations
      :companies
      :isAdmin="is_admin"
      :showCompanyColumn
      @view-details="(row) => handleViewDetails(row.id)"
      @download-documents="handleDownloadDocuments"
      @visible-change="(rows) => (visibleCancellations = rows)"
    />

    <!-- Add Cancellation Statement Modal -->
    <AddCancellationStatementModal
      v-if="showAddModal"
      :company_id="singleCompanyId"
      :companies="companies"
      :is_admin="is_admin"
      :initial-files="initialFilesForModal"
      @close="closeAddCancellationModal"
      @cancellation-created="handleCancellationCreated"
    />

    <!-- Cancellation Details Modal -->
    <CancellationDetailsModal
      v-if="showDetailsModal && selectedCancellationId"
      :cancellation-id="selectedCancellationId"
      :company_id="selectedCancellationCompanyId"
      :companies="companies"
      :is_admin="is_admin"
      @close="closeDetailsModal"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import {
  formatDateCompactUTC,
  isFullYearRange,
} from "@/composables/formatUtils";
import { useBulkCancellationDownload } from "@/composables/useBulkCancellationDownload";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import type { Cancellation, CancellationDocument } from "@/types/cancellation";
import {
  downloadFromBlob,
  extractFilenameFromContentDisposition,
  extractFilenameFromUrl,
  getFileExtension,
  sanitizeFilenameComponent,
} from "@/utils/downloadUtils";
import { computed, inject, onMounted, ref } from "vue";
import AddCancellationStatementModal from "./AddCancellationStatementModal.vue";
import CancellationDetailsModal from "./CancellationDetailsModal.vue";
import CancellationStatementsTable from "./CancellationStatementsTable.vue";

const props = defineProps<{
  companies: Company[];
  is_admin: boolean;
}>();

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

// State
const cancellations = ref<Cancellation[]>([]);
// The rows passing the table's filters. The list is current whenever the table is on screen: the
// table is only mounted with rows, and reports them during its first render because its filters
// object is non-empty.
const visibleCancellations = ref<Cancellation[]>([]);
const isLoading = ref(false);
const showAddModal = ref(false);
const showDetailsModal = ref(false);
const selectedCancellationId = ref<number | null>(null);
const selectedCancellationCompanyId = ref<number | null>(null);

// Drag and drop state
const isDraggingOver = ref(false);
const dragCounter = ref(0);
const initialFilesForModal = ref<File[]>([]);

// Computed properties
const showCompanyColumn = computed(() => {
  return props.companies.length > 1;
});

const isConsolidatedView = computed(() => {
  return props.companies.length > 1;
});

const singleCompanyId = computed(() => {
  return props.companies.length === 1 ? props.companies[0].id : null;
});

// Statement creation is trusted-companies-and-admins only (mirrors the server-side gate on
// POST /api/company/<id>/cancellations).
const canAddStatement = computed(
  () =>
    props.is_admin ||
    props.companies.some((company) => company.is_trusted === true)
);

const companyIds = computed(() => {
  return props.companies
    .map((company) => company.id)
    .filter(Boolean) as number[];
});

// Bulk download of the shown statements' documents - runs in the background (Celery) and is polled.
const {
  isBulkDownloading,
  canStart: canStartBulkDownload,
  blockedReason: bulkDownloadBlockedReason,
  label: bulkDownloadLabel,
  startBulkDownload,
} = useBulkCancellationDownload(visibleCancellations, () =>
  fetchCancellations()
);

/**
 * Generates a descriptive filename for cancellation documents
 * Format: {beneficiary_name}_{period}_{mwh_cancelled}MWh_{id}.{extension}
 *
 * Mirrored by build_document_basename in app/company_order/cancel/bulk_download.py, so a document
 * has the same name whether downloaded from its row or in the bulk ZIP. The id tells apart
 * statements that share beneficiary, period and volume, as a bulk batch often does.
 */
const generateCancellationFilename = (
  cancellation: Cancellation,
  extension: string
): string => {
  const sanitizedBeneficiary = sanitizeFilenameComponent(
    cancellation.beneficiary_name || ""
  );
  const beneficiary = sanitizedBeneficiary || "unknown";

  // Format period as YYYY or YYYY-MM-DD_YYYY-MM-DD
  let period: string;
  if (cancellation.period_start && cancellation.period_end) {
    const startDate = new Date(cancellation.period_start);
    const endDate = new Date(cancellation.period_end);

    if (isFullYearRange(startDate, endDate)) {
      period = String(startDate.getUTCFullYear());
    } else {
      period = `${formatDateCompactUTC(startDate)}_${formatDateCompactUTC(endDate)}`;
    }
  } else {
    period = "unknown-period";
  }

  const mwh = cancellation.mwh_cancelled ?? 0;

  const cleanExtension = extension.replace(/^\./, "");

  return `${beneficiary}_${period}_${mwh}MWh_${cancellation.id}.${cleanExtension}`;
};

const fetchCancellations = async () => {
  if (companyIds.value.length === 0) return;

  isLoading.value = true;
  try {
    const cancellationPromises = companyIds.value.map((companyId) =>
      goSolidApi.get(`/api/company/${companyId}/cancellations`)
    );

    const responses = await Promise.all(cancellationPromises);

    const allCancellations = responses.flatMap((response, index) => {
      if (response.status === 200 && response.data?.data?.cancellations) {
        return response.data.data.cancellations.map(
          (cancellation: Cancellation) => ({
            ...cancellation,
            company_id: companyIds.value[index],
          })
        );
      }
      return [];
    });

    cancellations.value = allCancellations
      .map((c: Cancellation) => ({
        ...c,
        period_key: `${c.period_start}|${c.period_end}`,
      }))
      .sort((a: Cancellation, b: Cancellation) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

// Drag and drop handlers
const resetDragState = () => {
  dragCounter.value = 0;
  isDraggingOver.value = false;
};

// Single gate for every drag entry point: drops are only accepted in the single-company view
// for accounts that may add statements; denied events also clear any half-tracked drag state.
const rejectDrag = (): boolean => {
  if (isConsolidatedView.value || !canAddStatement.value) {
    resetDragState();
    return true;
  }
  return false;
};

const handleDragEnter = (event: DragEvent) => {
  if (rejectDrag()) return;
  dragCounter.value++;
  if (event.dataTransfer?.types.includes("Files")) {
    isDraggingOver.value = true;
  }
};

const handleDragOver = (event: DragEvent) => {
  if (rejectDrag()) return;
  if (event.dataTransfer?.types.includes("Files")) {
    isDraggingOver.value = true;
  }
};

const handleDragLeave = () => {
  if (isConsolidatedView.value) return;
  dragCounter.value--;
  if (dragCounter.value === 0) {
    isDraggingOver.value = false;
  }
};

const handleDrop = (event: DragEvent) => {
  if (rejectDrag()) return;
  resetDragState();

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    initialFilesForModal.value = Array.from(files);
    showAddModal.value = true;
  }
};

// Modal handlers
const openAddCancellationModal = () => {
  initialFilesForModal.value = [];
  showAddModal.value = true;
};

const closeAddCancellationModal = () => {
  showAddModal.value = false;
  initialFilesForModal.value = [];
};

const handleCancellationCreated = () => {
  closeAddCancellationModal();
  fetchCancellations();
};

const handleViewDetails = (cancellationId: number) => {
  const cancellation = cancellations.value.find((c) => c.id === cancellationId);
  selectedCancellationId.value = cancellationId;
  selectedCancellationCompanyId.value = cancellation?.company_id || null;
  showDetailsModal.value = true;
};

const closeDetailsModal = () => {
  showDetailsModal.value = false;
  selectedCancellationId.value = null;
  selectedCancellationCompanyId.value = null;
};

const handleDownloadDocuments = async (cancellation: Cancellation) => {
  try {
    const companyId = cancellation.company_id || singleCompanyId.value;
    if (!companyId) {
      throw new Error("No valid company ID found");
    }

    // If multiple documents, download as ZIP
    if (cancellation.uploaded_document_count > 1) {
      const response = await goSolidApi.get(
        `/api/company/${companyId}/cancellations/${cancellation.id}/download-documents`,
        {
          responseType: "blob",
        }
      );

      const filename = generateCancellationFilename(cancellation, "zip");
      downloadFromBlob(response.data, filename);
      return;
    }

    // Single document - download directly
    const response = await goSolidApi.get(
      `/api/company/${companyId}/cancellations/${cancellation.id}`
    );

    if (response.status === 200 && response.data?.data?.uploaded_documents) {
      const documents = response.data.data.uploaded_documents;

      if (documents.length === 0) {
        return;
      }

      const doc = documents[0];
      await downloadDocument(doc, companyId, cancellation);
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const downloadDocument = async (
  doc: CancellationDocument,
  companyId: number,
  cancellation: Cancellation
) => {
  try {
    const response = await goSolidApi.get(
      `/api/company/${companyId}/download/${doc.id}`,
      {
        responseType: "blob",
      }
    );

    // The stored file's real name arrives in Content-Disposition — the authoritative extension
    // source (not every registry issues a PDF; e.g. Belgium Flanders returns a CSV). The file_url
    // basename is the fallback, and only then default to pdf.
    const dispositionFilename = extractFilenameFromContentDisposition(
      response.headers["content-disposition"] ||
        response.headers["Content-Disposition"]
    );
    const extension =
      getFileExtension(dispositionFilename || "") ||
      getFileExtension(extractFilenameFromUrl(doc.file_url)) ||
      "pdf";
    const filename = generateCancellationFilename(cancellation, extension);

    downloadFromBlob(response.data, filename);
  } catch (error) {
    console.error("Individual document download failed:", error);
    throw error;
  }
};

// Lifecycle
onMounted(() => {
  fetchCancellations();
});
</script>

<style scoped lang="scss">
.cancellation-statements-component {
  position: relative;
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  border: 3px dashed var(--phoenix-primary);
  border-radius: 0.5rem;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.drag-overlay-content {
  text-align: center;
  padding: 2rem;
  background-color: #ffffff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.drag-overlay-icon {
  font-size: 3rem;
  color: var(--phoenix-primary);
  margin-bottom: 1rem;
}

.drag-overlay-text {
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--phoenix-primary);
  margin-bottom: 0.5rem;
}
</style>
